"use client";

import {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useControlledState } from "./hooks";
import { cn } from "@/lib/cn";

// Local replacement for @datasirpi/ui's <AppSidebar> (Stage 5). The vendor
// component was a config-driven wrapper around its own lower-level
// <SideNav>/<SideNav.Section>/<SideNav.Item> primitives — but nothing in
// this app imports those primitives directly (only <AppSidebar> itself,
// confirmed by grep), so they're not reimplemented as a separate public
// API here; their rendering logic is inlined directly into this one
// component instead of maintaining an unused compound-component surface.
//
// Deliberately NOT ported: nested `item.children` groups (in-sidebar
// expand/collapse + the collapsed-mode hover flyout with its own
// positioning/timers/keyboard handling). Confirmed via the audit that
// `navSections` in Sidebar.tsx never nests — zero real usage anywhere in
// this app — so `AppSidebarItem` has no `children` field at all here,
// rather than exposing a prop that would silently do nothing.
//
// ── 2026-09-21: structure re-ported from the Firecrawl app shell ────────
// The vendor's *layout* is gone as of this pass; only the prop surface
// survives it. What changed, and why each change is structural rather
// than cosmetic (the CSS counterpart is the "App chrome" block in
// globals.css, which carries the rest of the reasoning):
//
//   • The rail is now header / scrolling nav / footer, and the RAIL no
//     longer scrolls — .rc-sidenav__scroll-area does. So the 64px brand
//     header and the collapse control stay pinned with ~40 nav items
//     behind them, instead of scrolling away with the list.
//   • Each row's icon lives in a 36px box absolutely pinned to the row's
//     left edge, so every icon sits on one optical axis regardless of
//     label length, and collapsing the rail moves nothing but its width.
//   • Hover and active are ONE absolutely-positioned pill each, behind
//     the whole list, slid by the effects below — not a background
//     repainted per row. The active pill therefore TRAVELS between
//     sections on navigation, which is the reference's single most
//     recognisable motion.
//   • The collapse control moved from a detached 28px circle floating
//     over the content beside the rail into a full-width labelled footer
//     button, so it is a real hit target and reads as part of the rail.
//     That also retires the <IconButton> dependency this file used to
//     carry for that one control.
export type SideNavTheme = "light" | "dark";

export interface AppSidebarItem {
  id: string;
  icon?: ReactNode;
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  badge?: ReactNode;
  /**
   * design.md §13: "Critical alert indicator: Small red dot, never use
   * animated flashing indicators." Distinct from `badge` (a count pill) —
   * this renders a small, static, non-animated dot anchored to the item's
   * icon box (so it stays visible in collapsed/icon-only mode too).
   */
  hasCriticalAlert?: boolean;
  active?: boolean;
  disabled?: boolean;
  /**
   * Nested items make this a disclosure group: a button that shows and hides
   * them. A group with an active child starts open, and clicking a group in
   * the collapsed rail expands the rail first. (UEM: the vendor sidebar had
   * groups and UEM's navigation uses them.)
   */
  children?: ReadonlyArray<AppSidebarItem>;
}

export interface AppSidebarSection {
  id: string;
  title?: ReactNode;
  items: ReadonlyArray<AppSidebarItem>;
}

export interface AppSidebarBrand {
  logo?: ReactNode;
  title?: ReactNode;
  href?: string;
}

export interface AppSidebarProfile {
  avatar?: ReactNode;
  name: ReactNode;
  role?: ReactNode;
}

export type AppSidebarRenderLink = (props: {
  href: string;
  "aria-current"?: "page";
  title?: string;
  className?: string;
  children: ReactNode;
}) => ReactElement;

export interface AppSidebarProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  brand?: AppSidebarBrand;
  profile?: AppSidebarProfile;
  items?: ReadonlyArray<AppSidebarItem>;
  sections?: ReadonlyArray<AppSidebarSection>;
  bottomItems?: ReadonlyArray<AppSidebarItem>;
  trailing?: ReactNode;
  showTrailingWhenCollapsed?: boolean;
  theme?: SideNavTheme;
  "aria-label"?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  toggleLabel?: (next: boolean) => string;
  renderLink?: AppSidebarRenderLink;
}

function defaultToggleLabel(next: boolean) {
  return next ? "Collapse sidebar" : "Expand sidebar";
}

/** Where a pill should sit, in px within the rail's positioning context. */
interface PillRect {
  top: number;
  height: number;
}

function rectOf(el: HTMLElement, rail: HTMLElement): PillRect {
  // Measured against the rail (the pills' own containing block) with
  // getBoundingClientRect rather than offsetTop, because offsetTop rounds to
  // whole pixels: rows land on fractional offsets often enough that the
  // rounded pill sat a quarter-pixel off its row and left a visible seam at
  // 2x. Both boxes scroll together inside .rc-sidenav__scroll-area, so the
  // difference between them is scroll-independent.
  const a = el.getBoundingClientRect();
  const b = rail.getBoundingClientRect();
  return { top: a.top - b.top, height: a.height };
}

// The pills are written straight to the DOM rather than through state. They
// are pure decoration derived from layout — nothing else renders from their
// position — and the hover pill in particular fires on every row crossing,
// which through state would re-render all ~40 rows per mouse move.
function applyPill(el: HTMLElement | null, rect: PillRect | null) {
  if (!el) return;
  if (!rect) {
    el.style.opacity = "0";
    return;
  }
  el.style.transform = `translateY(${rect.top}px)`;
  el.style.height = `${rect.height}px`;
  el.style.opacity = "1";
}

export const AppSidebar = forwardRef<HTMLDivElement, AppSidebarProps>(function AppSidebar(
  {
    brand,
    profile,
    items,
    sections,
    bottomItems,
    trailing,
    showTrailingWhenCollapsed = false,
    theme,
    "aria-label": ariaLabel = "Sidebar",
    collapsible = true,
    collapsed: collapsedProp,
    defaultCollapsed = false,
    onCollapseChange,
    toggleLabel = defaultToggleLabel,
    renderLink,
    className,
    ...rest
  },
  ref
) {
  const [collapsedState, setCollapsedState] = useControlledState<boolean>({
    ...(collapsedProp !== undefined ? { value: collapsedProp } : {}),
    defaultValue: defaultCollapsed,
    ...(onCollapseChange ? { onChange: onCollapseChange } : {}),
  });
  const collapsed = collapsedState ?? false;

  function toggleCollapsed() {
    setCollapsedState(!collapsed);
  }

  const renderedSections: ReadonlyArray<AppSidebarSection> =
    sections ?? (items ? [{ id: "__default", items }] : []);

  // ── Sliding pills ────────────────────────────────────────────────────
  const railRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const activePillRef = useRef<HTMLDivElement>(null);
  const hoverPillRef = useRef<HTMLDivElement>(null);

  // Keyed on the active item's id rather than on `renderedSections`, which
  // consumers rebuild on every render (Sidebar.tsx maps fresh objects each
  // time) — depending on the array identity would re-run this effect on
  // every render.
  const activeId =
    renderedSections.flatMap((s) => s.items.flatMap((it) => (it.children?.length ? it.children : [it]))).find((it) => it.active)?.id ?? null;

  // Which groups the user has opened or closed. A group nobody has touched is
  // open exactly when one of its children is the current page.
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const container = itemsRef.current;
    if (!container) return;

    let raf = 0;
    const sync = () => {
      const rail = railRef.current;
      if (!rail) return;
      const el = container.querySelector<HTMLElement>('[aria-current="page"]');
      applyPill(activePillRef.current, el ? rectOf(el, rail) : null);

      if (rail.dataset.pillReady !== "true") {
        // Flipped one frame AFTER the first placement — that flag is what
        // turns the pill's transition on (see globals.css), so without the
        // delay it would animate in from translateY(0) across the whole
        // rail on first paint.
        raf = requestAnimationFrame(() => {
          rail.dataset.pillReady = "true";
        });
      }
    };

    sync();

    // The nav's geometry also changes for reasons no dependency array sees:
    // Sidebar.tsx drops admin-only entries once /auth/me resolves, and web
    // fonts land a frame or two late. Both shift every row below the change,
    // which would otherwise leave the pill measured against a stale layout.
    if (typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(raf);
    }
    const ro = new ResizeObserver(sync);
    ro.observe(container);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [activeId, collapsed]);

  // Hover is delegated onto the list rather than bound per row, so no handler
  // has to travel through renderLink into each consumer's own link component.
  useEffect(() => {
    const container = itemsRef.current;
    if (!container) return;
    const onOver = (e: MouseEvent) => {
      const rail = railRef.current;
      const row = (e.target as HTMLElement | null)?.closest<HTMLElement>(".rc-sidenav__item");
      applyPill(hoverPillRef.current, rail && row && container.contains(row) ? rectOf(row, rail) : null);
    };
    const onLeave = () => applyPill(hoverPillRef.current, null);
    container.addEventListener("mouseover", onOver);
    container.addEventListener("mouseleave", onLeave);
    return () => {
      container.removeEventListener("mouseover", onOver);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // ── Rendering ────────────────────────────────────────────────────────
  function renderItem(it: AppSidebarItem): ReactNode {
    if (it.children && it.children.length > 0) {
      const open = !collapsed && (groupOpen[it.id] ?? it.children.some((c) => c.active));
      const groupId = `rc-sidenav-group-${it.id.replace(/[^A-Za-z0-9_-]/g, "-")}`;
      return (
        <div key={it.id} className="rc-sidenav__group" data-open={open || undefined}>
          <button
            type="button"
            className="rc-sidenav__item rc-sidenav__item--group"
            aria-expanded={open}
            aria-controls={open ? groupId : undefined}
            data-has-active={it.children.some((c) => c.active) || undefined}
            title={collapsed && typeof it.label === "string" ? it.label : undefined}
            onClick={() => {
              if (collapsed) {
                setCollapsedState(false);
                setGroupOpen((g) => ({ ...g, [it.id]: true }));
              } else {
                setGroupOpen((g) => ({ ...g, [it.id]: !open }));
              }
            }}
          >
            {it.icon ? (
              <span className="rc-sidenav__item-icon-wrap">
                <span className="rc-sidenav__item-icon" aria-hidden="true">
                  {it.icon}
                </span>
              </span>
            ) : null}
            <span className="rc-sidenav__item-body">
              <span className="rc-sidenav__item-label">{it.label}</span>
              <ChevronRight className="rc-sidenav__group-chevron" aria-hidden="true" />
            </span>
          </button>
          {open ? (
            <div id={groupId} role="group" className="rc-sidenav__group-items">
              {it.children.map((child) => renderItem(child))}
            </div>
          ) : null}
        </div>
      );
    }
    const body = (
      <>
        {it.icon ? (
          <span className="rc-sidenav__item-icon-wrap">
            <span className="rc-sidenav__item-icon" aria-hidden="true">
              {it.icon}
            </span>
            {it.hasCriticalAlert ? (
              <span
                className="rc-sidenav__item-critical-dot"
                role="status"
                aria-label="Critical alert"
              />
            ) : null}
          </span>
        ) : null}
        <span className="rc-sidenav__item-body">
          <span className="rc-sidenav__item-label">{it.label}</span>
          {it.badge != null ? <span className="rc-sidenav__item-badge">{it.badge}</span> : null}
        </span>
      </>
    );
    // The collapsed rail is icon-only, so a hovered icon would say nothing
    // about itself. This used to be a pure-CSS ::after off a data-label
    // attribute — but the rail's own scroll container is `overflow-x: hidden`
    // (it has to be), which clipped that tooltip before it cleared the 64px
    // rail, so it never actually appeared. A native title escapes every
    // overflow context and reaches assistive tech besides. Only set for
    // plain-string labels; a composite ReactNode label has no single string
    // to show, and only while collapsed — in the expanded rail the visible
    // label already says it.
    const hint = collapsed && typeof it.label === "string" ? it.label : undefined;

    if (it.href) {
      const linkProps = {
        href: it.href,
        className: "rc-sidenav__item",
        children: body,
        title: hint,
        ...(it.active ? { "aria-current": "page" as const } : {}),
      };
      return (
        <Fragment key={it.id}>
          {renderLink ? renderLink(linkProps) : <a {...linkProps}>{body}</a>}
        </Fragment>
      );
    }
    return (
      <button
        key={it.id}
        type="button"
        aria-current={it.active ? "page" : undefined}
        aria-disabled={it.disabled || undefined}
        className="rc-sidenav__item"
        title={hint}
        onClick={it.onClick}
      >
        {body}
      </button>
    );
  }

  const brandInner = brand ? (
    <>
      <span className="rc-sidenav__brand-logo">{brand.logo}</span>
      {brand.title ? <span className="rc-sidenav__brand-title">{brand.title}</span> : null}
    </>
  ) : null;

  const showTrailing = Boolean(trailing) && (!collapsed || showTrailingWhenCollapsed);
  const showBottom = Boolean(bottomItems?.length) || showTrailing;

  return (
    <div
      ref={ref}
      data-collapsed={collapsed || undefined}
      data-theme={theme}
      className={cn("rc-appsidebar", className)}
      {...rest}
    >
      <nav
        aria-label={ariaLabel}
        data-collapsed={collapsed || undefined}
        data-theme={theme}
        className="rc-sidenav"
      >
        {brand ? (
          <div className="rc-sidenav__header">
            {brand.href ? (
              <a href={brand.href} className="rc-sidenav__brand">
                {brandInner}
              </a>
            ) : (
              <div className="rc-sidenav__brand">{brandInner}</div>
            )}
          </div>
        ) : null}

        <div className="rc-sidenav__scroll-area">
          <div className="rc-sidenav__nav">
            <div className="rc-sidenav__rail" ref={railRef}>
              <div ref={activePillRef} className="rc-sidenav__pill rc-sidenav__pill--active" aria-hidden="true" />
              <div ref={hoverPillRef} className="rc-sidenav__pill rc-sidenav__pill--hover" aria-hidden="true" />
              <div ref={itemsRef} className="rc-sidenav__items">
                {renderedSections.map((s) => (
                  <Fragment key={s.id}>
                    {s.title ? <h6 className="rc-sidenav__section-title">{s.title}</h6> : null}
                    {s.items.map((it) => renderItem(it))}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {profile ? (
          <div className="rc-sidenav__footer">
            <div className="rc-sidenav__footer-row rc-sidenav__identity">
              <span className="rc-sidenav__footer-icon">
                <span className="rc-sidenav__identity-avatar">{profile.avatar}</span>
              </span>
              <span className="rc-sidenav__footer-label">
                <span className="rc-sidenav__identity-name">{profile.name}</span>
                {profile.role ? (
                  <span className="rc-sidenav__identity-role">{profile.role}</span>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}

        {showBottom ? (
          <div className="rc-sidenav__footer">
            {bottomItems?.map((it) => renderItem(it))}
            {showTrailing ? trailing : null}
          </div>
        ) : null}

        {collapsible ? (
          <div className="rc-sidenav__footer">
            <button
              type="button"
              className="rc-sidenav__footer-row rc-sidenav__collapse"
              aria-label={toggleLabel(!collapsed)}
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              <span className="rc-sidenav__footer-icon">
                <ChevronLeft aria-hidden="true" />
              </span>
              <span className="rc-sidenav__footer-label">Collapse</span>
            </button>
          </div>
        ) : null}
      </nav>
    </div>
  );
});
